import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Save, Image, Palette, Type, Layout, 
  RefreshCw, Eye, Plus, Trash2, GripVertical 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTheme } from '@/components/ThemeProvider';
import ImageUpload from '@/components/ImageUpload';

export default function SiteSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const { refreshTheme } = useTheme();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/site-settings');
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load site settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/site-settings', settings);
      setSettings(res.data);
      // Refresh theme to apply changes immediately
      refreshTheme();
      toast.success('Site settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateHero = (field, value) => {
    setSettings(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  const updateCTA = (field, value) => {
    setSettings(prev => ({
      ...prev,
      cta: { ...prev.cta, [field]: value }
    }));
  };

  const updateTheme = (field, value) => {
    setSettings(prev => ({
      ...prev,
      theme: { ...prev.theme, [field]: value }
    }));
  };

  const updateCollection = (index, field, value) => {
    setSettings(prev => {
      const newCollections = [...prev.collections];
      newCollections[index] = { ...newCollections[index], [field]: value };
      return { ...prev, collections: newCollections };
    });
  };

  const addCollection = () => {
    setSettings(prev => ({
      ...prev,
      collections: [
        ...prev.collections,
        { name: 'New Collection', image: '', description: 'Description here' }
      ]
    }));
  };

  const removeCollection = (index) => {
    if (settings.collections.length <= 1) {
      toast.error('You must have at least one collection');
      return;
    }
    setSettings(prev => ({
      ...prev,
      collections: prev.collections.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="site-settings-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-stone-900">Site Settings</h1>
              <p className="text-sm text-stone-500">Customize your website appearance</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm" data-testid="preview-site-btn">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              </Link>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                size="sm"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="hero" className="flex items-center gap-2">
              <Layout className="h-4 w-4" />
              Hero Section
            </TabsTrigger>
            <TabsTrigger value="collections" className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              Collections
            </TabsTrigger>
            <TabsTrigger value="cta" className="flex items-center gap-2">
              <Type className="h-4 w-4" />
              CTA Section
            </TabsTrigger>
            <TabsTrigger value="theme" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Theme Colors
            </TabsTrigger>
          </TabsList>

          {/* Hero Section Tab */}
          <TabsContent value="hero">
            <Card>
              <CardHeader>
                <CardTitle>Hero Section</CardTitle>
                <CardDescription>
                  Customize the main banner that visitors see when they land on your homepage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Background Image with Upload */}
                <ImageUpload
                  label="Background Image"
                  value={settings?.hero?.background_image || ''}
                  onChange={(url) => updateHero('background_image', url)}
                  maxSize={10}
                />

                {/* Accent Text */}
                <div className="space-y-2">
                  <Label>Accent Text (Small text above title)</Label>
                  <Input
                    value={settings?.hero?.accent_text || ''}
                    onChange={(e) => updateHero('accent_text', e.target.value)}
                    placeholder="Discover Elegance"
                    data-testid="hero-accent-input"
                  />
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <Label>Main Title</Label>
                  <Input
                    value={settings?.hero?.title || ''}
                    onChange={(e) => updateHero('title', e.target.value)}
                    placeholder="Premium Shawls for Every Occasion"
                    data-testid="hero-title-input"
                  />
                </div>

                {/* Subtitle */}
                <div className="space-y-2">
                  <Label>Subtitle</Label>
                  <Textarea
                    value={settings?.hero?.subtitle || ''}
                    onChange={(e) => updateHero('subtitle', e.target.value)}
                    placeholder="Explore our curated collection..."
                    rows={2}
                    data-testid="hero-subtitle-input"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Collections Tab */}
          <TabsContent value="collections">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Collections</CardTitle>
                    <CardDescription>
                      Manage the collection cards shown on the homepage
                    </CardDescription>
                  </div>
                  <Button onClick={addCollection} variant="outline" size="sm" data-testid="add-collection-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Collection
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {settings?.collections?.map((collection, index) => (
                  <div 
                    key={index} 
                    className="border rounded-lg p-4 space-y-4 bg-white"
                    data-testid={`collection-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <GripVertical className="h-5 w-5 text-stone-400" />
                        <span className="font-medium">Collection {index + 1}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeCollection(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-collection-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Image Upload */}
                      <ImageUpload
                        label="Collection Image"
                        value={collection.image}
                        onChange={(url) => updateCollection(index, 'image', url)}
                        maxSize={10}
                      />

                      {/* Details */}
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input
                            value={collection.name}
                            onChange={(e) => updateCollection(index, 'name', e.target.value)}
                            placeholder="Collection name"
                            data-testid={`collection-${index}-name`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={collection.description}
                            onChange={(e) => updateCollection(index, 'description', e.target.value)}
                            placeholder="Short description"
                            rows={3}
                            data-testid={`collection-${index}-desc`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CTA Section Tab */}
          <TabsContent value="cta">
            <Card>
              <CardHeader>
                <CardTitle>Call-to-Action Section</CardTitle>
                <CardDescription>
                  Customize the promotional banner at the bottom of the homepage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Background Image Upload */}
                <ImageUpload
                  label="CTA Background Image"
                  value={settings?.cta?.background_image || ''}
                  onChange={(url) => updateCTA('background_image', url)}
                  maxSize={10}
                />

                {/* Title */}
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={settings?.cta?.title || ''}
                    onChange={(e) => updateCTA('title', e.target.value)}
                    placeholder="New to Twenty Tulips?"
                    data-testid="cta-title-input"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={settings?.cta?.description || ''}
                    onChange={(e) => updateCTA('description', e.target.value)}
                    placeholder="Sign up for exclusive access..."
                    rows={2}
                    data-testid="cta-desc-input"
                  />
                </div>

                {/* Button Text */}
                <div className="space-y-2">
                  <Label>Button Text</Label>
                  <Input
                    value={settings?.cta?.button_text || ''}
                    onChange={(e) => updateCTA('button_text', e.target.value)}
                    placeholder="Create Account"
                    data-testid="cta-btn-input"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Theme Colors Tab */}
          <TabsContent value="theme">
            <Card>
              <CardHeader>
                <CardTitle>Theme Colors</CardTitle>
                <CardDescription>
                  Customize the color palette of your website
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Primary Color */}
                  <div className="space-y-3">
                    <Label>Primary Color</Label>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-lg border-2 border-stone-200 cursor-pointer"
                        style={{ backgroundColor: settings?.theme?.primary }}
                      >
                        <input
                          type="color"
                          value={settings?.theme?.primary || '#1c1917'}
                          onChange={(e) => updateTheme('primary', e.target.value)}
                          className="w-full h-full opacity-0 cursor-pointer"
                          data-testid="theme-primary-picker"
                        />
                      </div>
                      <Input
                        value={settings?.theme?.primary || ''}
                        onChange={(e) => updateTheme('primary', e.target.value)}
                        placeholder="#1c1917"
                        className="flex-1"
                        data-testid="theme-primary-input"
                      />
                    </div>
                    <p className="text-xs text-stone-500">Used for buttons, headers, and important elements</p>
                  </div>

                  {/* Secondary Color */}
                  <div className="space-y-3">
                    <Label>Secondary Color</Label>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-lg border-2 border-stone-200 cursor-pointer"
                        style={{ backgroundColor: settings?.theme?.secondary }}
                      >
                        <input
                          type="color"
                          value={settings?.theme?.secondary || '#e7e5e4'}
                          onChange={(e) => updateTheme('secondary', e.target.value)}
                          className="w-full h-full opacity-0 cursor-pointer"
                          data-testid="theme-secondary-picker"
                        />
                      </div>
                      <Input
                        value={settings?.theme?.secondary || ''}
                        onChange={(e) => updateTheme('secondary', e.target.value)}
                        placeholder="#e7e5e4"
                        className="flex-1"
                        data-testid="theme-secondary-input"
                      />
                    </div>
                    <p className="text-xs text-stone-500">Used for borders and secondary elements</p>
                  </div>

                  {/* Accent Color */}
                  <div className="space-y-3">
                    <Label>Accent Color</Label>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-lg border-2 border-stone-200 cursor-pointer"
                        style={{ backgroundColor: settings?.theme?.accent }}
                      >
                        <input
                          type="color"
                          value={settings?.theme?.accent || '#78716c'}
                          onChange={(e) => updateTheme('accent', e.target.value)}
                          className="w-full h-full opacity-0 cursor-pointer"
                          data-testid="theme-accent-picker"
                        />
                      </div>
                      <Input
                        value={settings?.theme?.accent || ''}
                        onChange={(e) => updateTheme('accent', e.target.value)}
                        placeholder="#78716c"
                        className="flex-1"
                        data-testid="theme-accent-input"
                      />
                    </div>
                    <p className="text-xs text-stone-500">Used for muted text and subtle highlights</p>
                  </div>

                  {/* Background Color */}
                  <div className="space-y-3">
                    <Label>Background Color</Label>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-lg border-2 border-stone-200 cursor-pointer"
                        style={{ backgroundColor: settings?.theme?.background }}
                      >
                        <input
                          type="color"
                          value={settings?.theme?.background || '#fafaf9'}
                          onChange={(e) => updateTheme('background', e.target.value)}
                          className="w-full h-full opacity-0 cursor-pointer"
                          data-testid="theme-bg-picker"
                        />
                      </div>
                      <Input
                        value={settings?.theme?.background || ''}
                        onChange={(e) => updateTheme('background', e.target.value)}
                        placeholder="#fafaf9"
                        className="flex-1"
                        data-testid="theme-bg-input"
                      />
                    </div>
                    <p className="text-xs text-stone-500">Main background color of the website</p>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-8 pt-6 border-t">
                  <Label className="mb-4 block">Preview</Label>
                  <div 
                    className="rounded-lg p-6"
                    style={{ backgroundColor: settings?.theme?.background }}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <button 
                        className="px-6 py-2 rounded-full text-white text-sm"
                        style={{ backgroundColor: settings?.theme?.primary }}
                      >
                        Primary Button
                      </button>
                      <button 
                        className="px-6 py-2 rounded-full text-sm border"
                        style={{ 
                          borderColor: settings?.theme?.secondary,
                          color: settings?.theme?.primary 
                        }}
                      >
                        Secondary Button
                      </button>
                    </div>
                    <p style={{ color: settings?.theme?.primary }}>
                      This is how your primary text will look
                    </p>
                    <p style={{ color: settings?.theme?.accent }}>
                      This is how your accent/muted text will look
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
